import React from 'react';
import styled, { css } from 'styled-components';

import OrigDiscountBadge from 'shared/components/discount-badge/discount-badge';

import { Product } from 'utils/helpers/product';

import ProductImage from 'components/product-image';
import { QuantityControls } from 'components/quantity-controls';
import { useListPriceTreatment } from 'src/hooks/use-list-price-treatment';

export const TEST_IDS = {
  quantityControls: `quantityControls`,
  productBrandName: `productBrandName`,
};

export enum BadgeType {
  offer = 'offer',
  sale = 'sale',
}

export type DiscountBadgeProps = {
  type: BadgeType;
  text: string;
};

export type CartItemDetailsProps = {
  badge?: DiscountBadgeProps;
  disableDecrementing: boolean;
  disableIncrementing: boolean;
  formattedDiscount?: string | null;
  formattedOption: string;
  formattedSpecialPrice?: string;
  formattedStandardPrice: string;
  handleDecrement: () => void;
  handleIncrement: () => void;
  product: Product;
  quantity: number;
};

export function CartItemDetails({
  badge,
  disableDecrementing,
  disableIncrementing,
  handleDecrement,
  handleIncrement,
  product,
  quantity,
  formattedDiscount,
  formattedOption,
  formattedSpecialPrice,
  formattedStandardPrice,
}: CartItemDetailsProps): JSX.Element {
  const { listPriceEnabled, hideStrikethrough, formatStandardPrice } = useListPriceTreatment();

  return (
    <Container>
      <ProductImage product={product} width={85} height={85} />

      <ProductDetailsContainer $listPriceEnabled={listPriceEnabled}>
        {badge && <DiscountBadge type={badge.type} displayText={formattedDiscount} />}

        {product.brand.name && (
          <ProductBrandName $listPriceEnabled={listPriceEnabled} data-testid={TEST_IDS.productBrandName}>
            {product.brand.name}
          </ProductBrandName>
        )}

        <ProductName $listPriceEnabled={listPriceEnabled}>{product.name}</ProductName>

        <ProductOptionAndPrice $listPriceEnabled={listPriceEnabled}>
          {formattedOption} @ {formattedSpecialPrice ?? formattedStandardPrice}&nbsp;/ea&nbsp;
          {/* hideStrikethrough is true only when both hideDiscountsFromConsumer and restrictSalesLanguage are enabled.
             In that case, the standard price still renders but without strikethrough styling (compliance mode). */}
          {formattedSpecialPrice && (
            <StrikedOutPrice $listPriceEnabled={listPriceEnabled} $hideSalesLanguage={hideStrikethrough}>
              {listPriceEnabled ? formatStandardPrice(formattedStandardPrice) : formattedStandardPrice}
            </StrikedOutPrice>
          )}
        </ProductOptionAndPrice>
      </ProductDetailsContainer>

      <QuantityControlsContainer>
        <StyledQuantityControls
          data-testid={TEST_IDS.quantityControls}
          disableIncrementing={disableIncrementing}
          disableDecrementing={disableDecrementing}
          onDecrement={handleDecrement}
          onIncrement={handleIncrement}
          quantity={quantity}
          isVisible
        />
      </QuantityControlsContainer>
    </Container>
  );
}

const StyledQuantityControls = styled(QuantityControls)`
  margin: auto;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
`;

const QuantityControlsContainer = styled.div`
  font-family: var(--dct--customized-fonts-secondary);
  position: relative;
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: 85px auto 40px;
  grid-gap: 5px;
  position: relative;
  text-align: left;
`;

const ProductDetailsContainer = styled.div<{ $listPriceEnabled?: boolean }>`
  display: grid;
  grid-auto-rows: min-content;
  justify-items: start;
  align-content: center;

  ${({ $listPriceEnabled }) =>
    $listPriceEnabled &&
    css`
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
    `}
`;

const listPriceBrandStyles = css`
  line-height: 165%;
  font-weight: 400;
  color: var(--dcm-product-detail-muted-color);
`;

const ProductBrandName = styled.span<{ $listPriceEnabled?: boolean }>`
  color: var(--dcm-product-brand-color);
  font-family: var(--dct--customized-fonts-secondary);
  font-size: 13px;
  line-height: 21px;
  ${({ $listPriceEnabled }) => $listPriceEnabled && listPriceBrandStyles}
`;

const ProductName = styled.span<{ $listPriceEnabled?: boolean }>`
  color: var(--dcm-product-name-color);
  font-size: 18px;
  font-weight: bold;
  line-height: 21px;

  ${({ $listPriceEnabled }) =>
    $listPriceEnabled &&
    css`
      font-weight: 700;
      line-height: 117%;
    `}
`;

const listPricePriceRowStyles = css`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  font-weight: 600;
  line-height: 175%;
  color: var(--dcm-product-name-color);
`;

const ProductOptionAndPrice = styled.span<{ $listPriceEnabled?: boolean }>`
  color: var(--dcm-product-detail-muted-color);
  font-family: var(--dct--customized-fonts-secondary);
  font-size: 14px;
  font-weight: bold;
  line-height: 24px;
  ${({ $listPriceEnabled }) => $listPriceEnabled && listPricePriceRowStyles}
`;

const listPriceStrikeStyles = css`
  font-size: 14px;
  line-height: 175%;
  font-weight: 600;
  color: var(--dcm-product-detail-muted-color);
`;

const StrikedOutPrice = styled.span<{ $listPriceEnabled?: boolean; $hideSalesLanguage?: boolean }>`
  color: var(--dcm-product-price-strikethrough-color);
  text-decoration-line: ${({ $hideSalesLanguage }) => ($hideSalesLanguage ? 'none' : 'line-through')};
  ${({ $listPriceEnabled }) => $listPriceEnabled && listPriceStrikeStyles}
`;

const DiscountBadge = styled(OrigDiscountBadge)`
  height: auto;
  margin-bottom: 3px;
`;
